/**
 * The Inventory window's words (P2.8-T11, ADR-091 decision 21): the item tooltip, drafted from the rules tables so
 * no number is written into the page, a slot's accessible name, and the window's own copy. The window's title and
 * its empty state are the design's (`ItemSurfaces.jsx`, the `inventory` card); the tooltip's wording and the slots'
 * names are drafts for the author, the design having none.
 *
 * `tools`: the tool row's words (P2.8-T45, ADR-125 decisions 2 and 4.2): the search field's placeholder and name,
 * "Search items", the design's; its clear cross (draft); Filter and its menu's heading "Show" and its entries, the
 * design's; the menu's own name (draft); and Sort, the design's.
 *
 * `menu`: the item menu's words (ADR-091's note for P2.8-T14 of the author's controls, ADR-125 decision 7.2): its
 * entries, "Consume" the author's own, "Split stack" and "Destroy" the design's; the reasons a disabled entry gives,
 * "Not a stack" and "Quest item" the design's and "No empty slot" a draft; "Keep in place" the design's and "Let it
 * move", for a stack already kept, a draft (ADR-125 decision 6.5); "Place on bar" the touch design's (ADR-121
 * decision 10); "Drop" the touch design's and the author's (P2.8-T34).
 *
 * `destroy`: the Destroy confirm's words (ADR-091's note for P2.8-T14 of the author's controls), drafts for the
 * author: its title asks, the same whatever the item; its body names the count and the item and says it cannot be
 * taken back; its confirm button names the action. `drop`: the Drop confirm's words (the author's ruling of
 * 2026-10-10 for P2.8-T34, "a confirmation on rare and above"), drafts.
 *
 * `sort`, `carry` and `card`: the Sort menu's heading and entries and the sentence a screen reader reads while a sort waits
 * (one for each order, so no grammar is left to the page), the carry number's tip and name (`{used}` and `{size}` are
 * numbers), and the item card's units, stat labels and footer hint. Drafts for the author but the design's hint.
 *
 * `{quantity}`, `{count}`, `{amount}` and `{percent}` are numbers as the page prints them; `{time}` arrives worded,
 * as "1.5 s".
 */
export const BAG = {
  title: "Inventory",
  emptyTitle: "Your bags are empty",
  emptyText: "Items you pick up, buy or loot appear here.",
  emptySlot: "Empty slot",
  lockedSlot: "Slot not in your bag",
  unknownItem: "Unknown item",
  slots: "Bag slots",
  kept: "kept in place",
  sort: {
    heading: "Sort bags by",
    orders: { type: "Type", rarity: "Rarity", name: "Name", newest: "Newest first" },
    waiting: {
      type: "Sorting by type: waiting for the server.",
      rarity: "Sorting by rarity: waiting for the server.",
      name: "Sorting by name: waiting for the server.",
      newest: "Sorting by newest first: waiting for the server.",
    },
  },
  carry: {
    tip: "Bag slots used",
    name: "Bag slots used: {used} of {size}",
  },
  card: {
    health: "Health",
    stamina: "Stamina",
    againstPlayers: "Against players",
    cooldown: "Cooldown",
    hint: "Right click for actions",
  },
  tools: {
    search: "Search items",
    clear: "Clear search",
    filter: "Filter",
    filterHeading: "Show",
    filterMenu: "Show items",
    sort: "Sort",
    filters: {
      all: "All",
      equipment: "Gear",
      consumable: "Consumables",
      material: "Materials",
      quest: "Quest",
    },
  },
  menu: {
    consume: "Consume",
    place: "Place on bar",
    belt: "Put on belt",
    split: "Split stack",
    keep: "Keep in place",
    letMove: "Let it move",
    drop: "Drop",
    destroy: "Destroy",
    notStack: "Not a stack",
    noRoom: "No empty slot",
    quest: "Quest item",
  },
  heading: "{name} ({quantity})",
  destroy: {
    title: "Destroy this item?",
    confirm: "Destroy",
    cancel: "Cancel",
    body: "{count} {name} will be gone for good. This cannot be undone.",
  },
  drop: {
    body: "Anyone nearby can pick it up.",
    confirm: "Drop",
    cancel: "Cancel",
    title: "Drop {name}?",
    titleCounted: "Drop {name} x{count}?",
  },
  refused: {
    duel: "Not in a duel.",
    move: "The server refused that move. Nothing changed.",
    destroy: "The stack changed. Nothing was destroyed.",
    dropGround: "There is no room on the ground here.",
    dropChanged: "The stack changed. Nothing was dropped.",
  },
  moving: "Moving the {name}: waiting for the server.",
  barCombat: "When fighting players, potions restore {percent}% less",
  kinds: {
    consumable: "Consumable",
    equipment: "Equipment",
    material: "Material",
    quest: "Quest item",
  },
  counted: "{name}, {quantity}",
  keptSlot: "{slot}, {kept}",
} as const;
